import { useContext } from 'react';
// import {} from '@europrocurement/l2d-domain/openApi/ApiTiers';

import { getDataThunkType, getItemThunkType } from '@europrocurement/l2d-redux-utils';

import {
    AntennesUtilisateurApiObject,
    GroupsUtilisateurApiObject,
    MarquesUtilisateurApiObject,
    PolesUtilisateurApiObject,
    ServicesUtilisateurApiObject,
    DomainesUtilisateurApiObject,
    GroupeTravailUtilisateurApiObject,
    UtilisateurGroupeTravailUtilisateurApiObject,
    UtilisateurApiObject,
    UtilisateursReducerType,
} from '@europrocurement/l2d-domain/reducers/utilisateurs';

import {
    AntennesApi,
    AvatarsApi,
    DroitsApi,
    GroupesApi,
    JournauxApi,
    MarquesApi,
    PolesApi,
    ServicesApi,
    UserApi,
    UtilisateursApi,
    GroupesDeTravailApi,
    DomaineApi,
    JointureUtilisateurGroupeTravailApi,
} from '@europrocurement/l2d-domain/openApi/ApiUtilisateur';
import { APIENV, ENV_API } from '@europrocurement/l2d-domain/constants';
import { getCustomiserState } from '@europrocurement/l2d-domain/utils';
import { defaultEnv } from '@europrocurement/l2d-domain/reducers/customizer/defaultEnv';
import L2DApiContext from '../L2DApiContext';

export const useUtilisateurService = () => {
    const context = useContext(L2DApiContext);
    if (!context) {
        throw new Error('useL2DApi must be used within a L2DApiProvider');
    }

    if (!context.utilisateur) {
        throw new Error('Utilisateurs service is not registred in L2DApiProvider');
    }

    const { apis, slices, thunks, fetchItems, getItems, reducer } = context.utilisateur;

    return {
        reducer: reducer as UtilisateursReducerType,

        // Apis
        utilisateurApi: apis.utilisateursApi as UtilisateursApi,
        userKeycloakApi: apis.userKeycloakApi as UserApi,
        polesApi: apis.polesApi as PolesApi,
        droitsApi: apis.droitsApi as DroitsApi,
        groupesApi: apis.groupesApi as GroupesApi,
        journauxApi: apis.journauxApi as JournauxApi,
        marquesApi: apis.marquesApi as MarquesApi,
        servicesApi: apis.servicesApi as ServicesApi,
        antennesApi: apis.antennesApi as AntennesApi,
        domainesApi: apis.domainesApi as DomaineApi,
        groupesTravailApi: apis.groupesTravailApi as GroupesDeTravailApi,
        utilisateursGroupeTravailApi:
            apis.utilisateurGroupeTravailApi as JointureUtilisateurGroupeTravailApi,
        avatarsApi: apis.avatarsApi as AvatarsApi,

        // Slices
        utilisateurSlice: slices.utilisateurSlice,
        servicesSlice: slices.servicesSlice,
        polesSlice: slices.polesSlice,
        marquesSlice: slices.marquesSlice,
        groupesSlice: slices.groupesSlice,
        antennesSlice: slices.antennesSlice,
        domainesSlice: slices.domainesSlice,
        groupeTravailSlice: slices.groupeTravailSlice,
        utilisateursGroupeTravailSlice: slices.utilisateurGroupeTravailSlice,

        // Thunks
        utilisateurDataSourcesThunks: thunks.utilisateurDataSourcesThunks,
        servicesDataSourcesThunks: thunks.servicesDataSourcesThunks,
        polesDataSourcesThunks: thunks.polesDataSourcesThunks,
        marquesDataSourcesThunks: thunks.marquesDataSourcesThunks,
        groupesDataSourcesThunks: thunks.groupesDataSourcesThunks,
        antennesDataSourcesThunks: thunks.antennesDataSourcesThunks,
        domainesDataSourcesThunks: thunks.domainesDataSourcesThunks,
        groupeTravailDataSourcesThunks: thunks.groupeTravailDataSourcesThunks,
        utilisateursGroupeTravailDataSourcesThunks:
            thunks.utilisateursGroupeTravailDataSourcesThunks,

        // FetchItems
        getUtilisateurs: fetchItems.getUtilisateurs as getDataThunkType<UtilisateurApiObject>,
        getAutoSearch: fetchItems.getAutoSearch as getDataThunkType<UtilisateurApiObject>,
        getServices: fetchItems.getServices as getDataThunkType<ServicesUtilisateurApiObject>,
        getPoles: fetchItems.getPoles as getDataThunkType<PolesUtilisateurApiObject>,
        getMarques: fetchItems.getMarques as getDataThunkType<MarquesUtilisateurApiObject>,
        getGroupes: fetchItems.getGroupes as getDataThunkType<GroupsUtilisateurApiObject>,
        getAntennes: fetchItems.getAntennes as getDataThunkType<AntennesUtilisateurApiObject>,
        getDomaines: fetchItems.getDomaines as getDataThunkType<DomainesUtilisateurApiObject>,
        getGroupesTravail:
            fetchItems.getGroupesTravail as getDataThunkType<GroupeTravailUtilisateurApiObject>,
        getUtilisateursGroupeTravail:
            fetchItems.getUtilisateurGroupeTravail as getDataThunkType<UtilisateurGroupeTravailUtilisateurApiObject>,
        getGroupeTravailActiveInscriptions:
            fetchItems.getGroupeTravailActiveInscriptions as getDataThunkType<UtilisateurGroupeTravailUtilisateurApiObject>,

        // SelectItems
        selectUtilisateurs: getItems.selectUtilisateurs as getItemThunkType<UtilisateurApiObject>,
        selectAntennes: getItems.selectAntennes as getItemThunkType<AntennesUtilisateurApiObject>,
        selectAutoSearch: getItems.selectAutoSearch as getItemThunkType<UtilisateurApiObject>,
        selectGroupeTravail:
            getItems.selectGroupesTravail as getItemThunkType<GroupeTravailUtilisateurApiObject>,

        avatarBaseUrl: (APIENV.apis.s3avatar[getCustomiserState().api.s3avatar as ENV_API] ||
            APIENV.apis.s3avatar[defaultEnv]) as string,
    };
};

export const useUtilisateurAvatarUrl = (): string =>
    APIENV.apis.s3avatar[getCustomiserState().api.s3avatar as ENV_API] ||
    APIENV.apis.s3avatar[defaultEnv] ||
    '';
